import React, {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import api from "../../utils/axios";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import styles from "./RegisterOrderForm/RegisterOrderForm.module.css";
import DefaultInput from "../../components/form/DefaultInput";
import CurrencySelectField from "../../components/form/CurrencySelectField";
import TextArea from "antd/es/input/TextArea";
import Buttons from "../../components/form/Buttons";
import { extractQueryParameters } from "../../utils/extractQueryParameters";
import { LatLngTuple } from "leaflet";
import {
	Button,
	Cascader,
	Checkbox,
	ConfigProvider,
	Divider,
	Input,
	Modal,
	Select,
	SelectProps,
	ThemeConfig,
	Upload,
	UploadFile,
} from "antd";
import { toast } from "react-toastify";
import { fetchZones, getConfigurationValue } from "../../lib/OptionListApi";
import useCurrencies from "../../hooks/useCurrencies";
import { currencySymbol } from "../../utils/formatters";
import {
	COUNTRY_AL,
	COUNTRY_KS,
	COUNTRY_MK,
	CURRENCY_EUR,
} from "../../api/currency";
import { useNavigate } from "react-router";
import useLang from "../../hooks/useLang";
import { ReactComponent as AlertIcon } from "../../assets/AlertIcon.svg";
import { ReactComponent as ExchangeIcon } from "../../assets/Exchange1.svg";
import { ReactComponent as InvoiceIcon } from "../../assets/Invoice1.svg";
import { ReactComponent as ReturnableIcon } from "../../assets/Returnable.svg";
import {
	FileTextOutlined,
	InboxOutlined,
	SearchOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { CONFIG } from "../../config";
import AddressSelect from "../../components/form/AddressSelect";
import {
	BRAND_PRIMARY,
	PRIMARY_BUTTON_STYLE,
} from "../BulkOrderOperations/components/styles";
import FormSection from "./RegisterOrderForm/FormSection";
import FormField from "./RegisterOrderForm/FormField";
import CollapsibleSection from "./RegisterOrderForm/CollapsibleSection";
import OrderTypePicker, {
	OrderTypeOption,
} from "./RegisterOrderForm/OrderTypePicker";
import PriceSummary from "./RegisterOrderForm/PriceSummary";
import DeliveryMap from "./RegisterOrderForm/DeliveryMap";

interface IOrderInsertData {
	[name: string]: any;
}

interface Zone {
	id: number;
	description: string;
	levelId: number;
	parentId: any;
}

type PackageWeightDefaults = {
	id: number;
	from: number;
	to?: number;
	price?: number;
	number?: number;
};

type PackageValueDefaults = {
	id: number;
	from: number;
	to?: number;
	percentage?: number;
	number?: number;
};

/**
 * Identifies the band set the tariff state holds. Country as well as currency, because changing
 * either invalidates the bands just as completely — and the zone, since tariffs became configurable
 * per zone: a Tirana section resolves to a different band set than Albania as a whole.
 */
const tariffKey = (countryId: number, currencyId?: number, zoneId?: number) =>
	`${countryId}:${currencyId ?? "default"}:${zoneId ?? "country"}`;

/**
 * The form's antd theme: brand accent on focus rings, radios, checkboxes and the primary button,
 * and the control height the rest of the app's inputs have. Same approach as
 * BulkDriverPayoutModal.
 */
const FORM_THEME: ThemeConfig = {
	token: {
		colorPrimary: BRAND_PRIMARY,
		controlHeight: 42,
		borderRadius: 8,
	},
};

/**
 * The code the street search is scoped by, keyed by the selected country's id.
 *
 * Derived from the selection rather than kept as state of its own. As state it was only updated by
 * the country dropdown, so picking a saved buyer left it on whichever country was chosen before —
 * "xk" on a fresh page — and the street search then looked for an Albanian buyer's street in
 * Kosovo and found nothing.
 */
const ADDRESS_SEARCH_COUNTRY: Record<number, string> = {
	[COUNTRY_KS]: "xk",
	[COUNTRY_AL]: "al",
	[COUNTRY_MK]: "mk",
};

/**
 * The customs code an order abroad is registered with when the seller picks none. The field is
 * optional so that sellers who do not deal with customs can leave it; this is what those orders
 * carry instead of nothing.
 */
const DEFAULT_CUSTOMS_CODE = "85271200";

/**
 * Keeps Chrome's saved-address popup off the country-and-city search box.
 *
 * Chrome reads that box as an address field and opens its own autofill list over the dropdown. It
 * ignores "off" — antd's default — but not a value it does not recognise. Spread rather than
 * written as a prop because antd's Cascader types omit `autoComplete`, although rc-select passes it
 * through to the input.
 */
const NO_BROWSER_AUTOFILL = { autoComplete: "no-autofill-location" };

/** How a customs row reads, in the dropdown and as its placeholder. */
const customsLabel = (costum: any) =>
	`${costum.costumsCode} -  ${costum.description}`;

type OrderType = "standard" | "exchangeable" | "returnable" | "invoice";

/** Letters and spaces — what the buyer-name field has always accepted. */
const FULL_NAME_ALLOWED = /^[a-zA-Z ]*$/;

/**
 * The text an edit added to a field: whatever lies between the part the old and new value share at
 * the start and the part they share at the end. Empty for a pure deletion.
 */
const insertedText = (previous: string, next: string) => {
	let start = 0;
	while (
		start < previous.length &&
		start < next.length &&
		previous[start] === next[start]
	) {
		start++;
	}
	let end = 0;
	while (
		end < previous.length - start &&
		end < next.length - start &&
		previous[previous.length - 1 - end] === next[next.length - 1 - end]
	) {
		end++;
	}
	return next.slice(start, next.length - end);
};

/**
 * Text the way the form's searches compare it: lower case and without diacritics, so "prishtine"
 * finds "Prishtinë" and "korce" finds "Korçë" — most people type without ë and ç.
 */
const searchKey = (text: unknown) =>
	String(text ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

/** A map position from stored coordinates, or null when either is missing or not a number. */
const toMapPosition = (
	latitude: unknown,
	longitude: unknown
): LatLngTuple | null => {
	const isBlank = (value: unknown) =>
		value === null || value === undefined || value === "";
	if (isBlank(latitude) || isBlank(longitude)) return null;
	const position: LatLngTuple = [Number(latitude), Number(longitude)];
	return position.every(Number.isFinite) ? position : null;
};

/**
 * What was typed into a decimal field, with a comma read as the decimal point — or null to refuse
 * the keystroke.
 *
 * iOS puts the region's decimal separator on an inputMode="decimal" keypad: a comma in Albania,
 * Kosovo and North Macedonia, with no dot key at all. A type="number" input refuses that comma, so
 * on an iPhone no decimal could be typed. These fields are type="text" with the decimal keypad
 * instead, and take either separator.
 */
const readDecimalInput = (raw: string): string | null => {
	const value = raw.replace(",", ".");
	return /^\d*\.?\d*$/.test(value) ? value : null;
};

const Register = () => {
	const currentURL = window.location.search;
	const queryParams = extractQueryParameters(currentURL);
	const buyerId = queryParams.id;
	const navigate = useNavigate();
	const fieldId = useId();

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [address, setAddress] = useState("");
	const [sectionId, setSectionId] = useState<number | null>(0);
	const [phone, setPhone] = useState("");
	// No longer an input — it was dropped from the form — but a saved buyer's email is still carried
	// through to the save, so registering an order for them does not blank it.
	const [email, setEmail] = useState("");
	const [addressDetails, setAddressDetails] = useState("");
	const [width, setWidth] = useState(20);
	const [length, setLength] = useState(20);
	const [height, setHeight] = useState(20);
	const [weight, setWeight] = useState(0.5);
	const [openable, setOpenable] = useState(true);
	const [isExistingPhoneNumberVisible, setIsExistingPhoneNumberVisible] =
		useState<boolean>(false);
	const [fragile, setFragile] = useState(false);
	const [declared, setDeclared] = useState(false);
	const [invoice, setInvoice] = useState(false);
	const [exchangeable, setExchangeable] = useState(false);
	const [deliveryInstructions, setDeliveryInstructions] = useState("");
	const [orderPrice, setOrderPrice] = useState<any>("");
	// ⚠️ Legacy. This has always been a hardcoded 1 that the API throws away; it is NOT a
	// currency. Left untouched — orderCurrencyId below is what carries the real choice.
	const [currencyId, setCurrencyId] = useState(1);
	// Declared up here, out of order with the rest of the buyer fields, because the destination
	// country is what decides whether the currency may be chosen at all — see currencyLocked below.
	const [selectedCountry, setSelectedCountry] = useState<
		string | number | undefined
	>();
	const {
		// Exactly the two currencies an order may be registered in — the deployment's own plus EUR,
		// never the raw Currency/list, which carries all three rows so convert() can resolve any
		// pair. The filter lives in the hook because the order-details currency correction needs
		// the identical rule.
		selectableCurrencies,
		countryId: deploymentCountryId,
		dualCurrencyEnabled,
		defaultCurrencyId,
		convert,
		loading: currenciesLoading,
	} = useCurrencies();
	// The operator's own pick. What the order is actually registered in is `orderCurrencyId` further
	// down, which overrides this wherever the destination country does not allow a choice.
	const [chosenOrderCurrencyId, setChosenOrderCurrencyId] = useState<
		number | undefined
	>(undefined);

	// Default to the deployment currency as soon as the list resolves.
	useEffect(() => {
		if (defaultCurrencyId) setChosenOrderCurrencyId(defaultCurrencyId);
	}, [defaultCurrencyId]);

	/**
	 * Where the parcel is going. Nothing picked yet counts as the deployment's own country — the
	 * same fallback the tariff loader makes (see tariffCountryId), so the form opens on the state it
	 * will price in.
	 *
	 * The dropdown carries level-1 **zone** ids, which are the country ids: the equivalence
	 * ADDRESS_SEARCH_COUNTRY and the customs-code gate below already rely on.
	 */
	const destinationCountryId = selectedCountry
		? Number(selectedCountry)
		: deploymentCountryId;

	/**
	 * Whether the destination, not the operator, decides the currency.
	 *
	 * The local currency is only offered on orders that stay inside the deployment's own country.
	 * LEK is legal tender in Albania and nowhere else this platform delivers to — a parcel to Kosovo
	 * or Macedonia is collected from the buyer, settled and paid out in EUR. With the selector left
	 * free, a Pristina delivery could be registered in LEK: priced off the LEK tariff bands (~96x
	 * out) and then carried as a LEK figure on a balance that is only ever settled in EUR, which no
	 * downstream screen can unpick because nothing converts between the two.
	 *
	 * Gated on `dualCurrencyEnabled` first, so Kosovo and Macedonia deployments are untouched: there
	 * the local currency IS the only currency, and forcing EUR would relabel every amount on the
	 * form. Compared by countryId, never by currency symbol — trap 3 in CLAUDE.md.
	 */
	const currencyLocked =
		dualCurrencyEnabled && destinationCountryId !== deploymentCountryId;

	/**
	 * The currency this order is registered in: the operator's pick unless the destination overrides
	 * it.
	 *
	 * Derived rather than written back into state when the country changes. Writing it back means a
	 * render where the form is already pointed at Kosovo while the currency is still LEK — and the
	 * tariff effect fires on that pair, fetching bands nobody asked for — and it would also destroy
	 * a deliberate EUR pick on an Albanian order the moment the operator corrected the country twice.
	 * Everything downstream reads this: the tariff key, the save payload, every symbol, the
	 * returnable-balance gate.
	 */
	const orderCurrencyId = currencyLocked
		? CURRENCY_EUR
		: chosenOrderCurrencyId;

	// The other currency, for the sanity-check line under the input. Drawn from the filtered set
	// so it can never land on a currency the operator was not offered.
	const otherCurrencyId = useMemo(
		() => selectableCurrencies.find((x) => x.id !== orderCurrencyId)?.id,
		[selectableCurrencies, orderCurrencyId]
	);

	const convertedOrderPrice = useMemo(
		() =>
			orderPrice === "" || orderPrice === null
				? null
				: convert(Number(orderPrice), orderCurrencyId, otherCurrencyId),
		[orderPrice, orderCurrencyId, otherCurrencyId, convert]
	);
	const [orderDescription, setOrderDescription] = useState("");
	const [packageDescription, setPackageDescription] = useState("");
	const [zones, setZones] = useState<Zone[]>([]);
	// selectedCountry is declared with the currency state above, which depends on it.
	const [selectedCity, setSelectedCity] = useState<
		string | undefined | number
	>();
	const [fullName, setFullName] = useState("");
	const [fromBuyerPage, setFromBuyerPage] = useState(false);
	const [temporaryBuyerDetailsId, setTemporaryBuyerDetailsId] = useState();
	const [temporaryBuyerDetails, setTemporaryBuyerDetails] = useState();
	// The antd Upload's own file list, controlled so that removing a photo really removes it from
	// what is sent, and so the Cancel reset clears it.
	const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
	const [refId, setRefId] = useState<string | null>(null);
	const [ordersRealPrice, setOrdersRealPrice] = useState<any>(0);
	const [waitingPickup, setWaitingPickup] = useState<boolean>(false);
	const [openVatModal, setOpenVatModal] = useState<boolean>(false);
	const [vat, setVat] = useState<any>(0);
	const [orderPriceNoVat, setOrderPriceNoVat] = useState<any>(0);
	const [buyerDropDown, setBuyerDropDown] = useState<any[]>();
	const [searchText, setSearchText] = useState<string>("");
	const [costums, setCostums] = useState([]);

	// The pin on the delivery map. Display only — the save sends apiLatitude/apiLongitude.
	const [position, setPosition] = useState<LatLngTuple | null>(null);
	const [geoData, setGeoData] = useState<any>();
	const [apiLatitude, setApiLatitude] = useState<number | null>();
	const [apiLongitude, setApiLongitude] = useState<number | null>();

	const [nameError, setNameError] = useState<boolean>(false);
	// Set when a keystroke is refused by FULL_NAME_ALLOWED, so the field can say why.
	const [nameCharError, setNameCharError] = useState<boolean>(false);
	const [addressError, setAddressError] = useState<boolean>(false);
	const [addressDetailsError, setAddressDetailsError] =
		useState<boolean>(false);
	const [packageValueError, setPackageValueError] = useState<boolean>(false);
	const [phoneError, setPhoneError] = useState<boolean>(false);
	const [openOrdersRealPriceModal, setOpenOrdersRealPriceModal] =
		useState<boolean>(false);
	const [packageDescriptionError, setPackageDescriptionError] =
		useState<boolean>(false);
	const [countryError, setCountryError] = useState<boolean>(false);
	const [cityError, setCityError] = useState<boolean>(false);

	// Every optional field sits in one group that starts closed, so the form opens on the
	// required ones only.
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const [packageWeightDefaults, setPackageWeightDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [packageVolumeDefaults, setPackageVolumeDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [packageValueDefaults, setPackageValueDefaults] = useState<
		PackageValueDefaults[]
	>([]);

	/**
	 * Which (country, currency) pair the bands currently in state were fetched for, or null while
	 * they are in flight or failed.
	 *
	 * Without this the form had no way to tell "these bands are for the currency I am asking
	 * about" from "these bands are whatever was loaded last" — which is how a EUR order came to be
	 * priced from LEK bands during, and after, a failed switch.
	 */
	const [loadedTariffKey, setLoadedTariffKey] = useState<string | null>(null);
	const [tariffError, setTariffError] = useState(false);
	/** Only the newest tariff request may commit; see loadTariffBands. */
	const tariffRequestRef = useRef(0);
	const [customsId, setCustomsId] = useState<number | null>(null);
	const [data, setData] = useState<SelectProps["options"]>([]);
	const [systemCountryId, setSystemCountryId] = useState<any>();
	const [vatValues, setVatValues] = useState<any>([]);
	const [returnable, setReturnable] = useState(false);

	/**
	 * Countries with their cities under them, for the one country-and-city field, and the section
	 * list — all derived from the zones rather than stored.
	 *
	 * The city list used to be state, re-filtered only by the country dropdown — so picking a saved
	 * buyer left it on the previous country's cities, and the city field then showed a bare id.
	 */
	const locationOptions = useMemo(
		() =>
			zones
				.filter((zone) => zone.levelId === 1)
				.map((country) => ({
					value: country.id,
					label: country.description,
					children: zones
						.filter(
							(zone) =>
								zone.levelId === 2 &&
								zone.parentId === country.id
						)
						.map((city) => ({
							value: city.id,
							label: city.description,
						})),
				})),
		[zones]
	);
	const allSections = useMemo(
		() => zones.filter((zone) => zone.levelId === 3),
		[zones]
	);

	const addressSearchCountry = selectedCountry
		? ADDRESS_SEARCH_COUNTRY[Number(selectedCountry)] ?? ""
		: "";

	// Outside the deployment's country the form asks for a customs code — optionally; see
	// orderCustomsId.
	const isInternational =
		Boolean(selectedCountry) && selectedCountry !== systemCountryId;

	/** The customs row carrying DEFAULT_CUSTOMS_CODE, if this deployment's list has one. */
	const defaultCustoms = useMemo(
		() =>
			(costums as any[]).find(
				(costum) =>
					String(costum?.costumsCode ?? "").trim() ===
					DEFAULT_CUSTOMS_CODE
			),
		[costums]
	);

	/**
	 * The customs code the order is saved with: the seller's pick or, on an order abroad left
	 * without one, the default code.
	 *
	 * Sent as the row's id — the API stores `CustomsId` and joins it to `Costums.Id`, so the code
	 * itself would be read as an id. Null when the default code is missing from this deployment's
	 * list, which the API accepts: the order still registers, just without a code. A domestic order
	 * carries whatever was picked, as before — normally nothing, since the field is not shown.
	 */
	const orderCustomsId = isInternational
		? customsId ?? defaultCustoms?.id ?? null
		: customsId;

	const getSystemDefaultCountryId = async () => {
		try {
			setSystemCountryId(parseInt(await getConfigurationValue(2)));
		} catch (error: any) {
			console.log(error);
		}
	};

	const getBuyers = async () => {
		try {
			const response = await api.get("Buyer/select-buyers");
			if (response.status === 200) {
				const result = response?.data;
				setBuyerDropDown(result);
			}
		} catch (error) {
			console.log(error);
		}
	};
	const getVatSelection = async () => {
		try {
			const response = await api.get("Currency/select-vat");
			if (response.status === 200) {
				const result = response?.data;
				setVatValues(result);
			}
		} catch (error) {
			console.log(error);
		}
	};

	/**
	 * Loads the three tariff band sets for one (country, currency) pair, atomically.
	 *
	 * The bands are per-currency: the EUR tables hold EUR-scale prices (1.56, 2.34, 3.13…) and the
	 * LEK tables LEK-scale ones (150, 225, 300…) — and the *value* bands' own `from`/`to`
	 * boundaries are denominated in their own currency too, so an order priced against the wrong
	 * set does not merely get the wrong rate, it matches the wrong band. That is a ~96x error, and
	 * it feeds the returnable-balance gate as well as the figure on screen.
	 *
	 * The currency is threaded explicitly rather than read from state, because the selector's
	 * onChange loads immediately, before its setState has landed. Same reason
	 * PriceConfigurations.tsx does it this way.
	 */
	const loadTariffBands = useCallback(
		async (
			selectedCountryId: number,
			selectedCurrencyId?: number,
			selectedZoneId?: number
		) => {
			// Off PostmanAL the parameter is omitted entirely, so the request is byte-identical
			// to what it has always been.
			const currencyParam = dualCurrencyEnabled
				? selectedCurrencyId
				: undefined;
			// Likewise: no section resolved yet means no zoneId, and the API answers with the
			// country's bands exactly as before. Axios drops undefined params.
			const zoneParam = selectedZoneId || undefined;
			const requestId = ++tariffRequestRef.current;

			// Invalidate BEFORE awaiting. Left in place, the previous currency's bands price the
			// new one for as long as the fetch takes — and forever if it never succeeds.
			setLoadedTariffKey(null);
			setTariffError(false);

			try {
				const params = {
					countryId: selectedCountryId,
					currencyId: currencyParam,
					zoneId: zoneParam,
				};
				const [weight, volume, value] = await Promise.all([
					api.get("packagedefaults/client-weight-list", { params }),
					api.get("packagedefaults/client-volume-list", { params }),
					api.get("packagedefaults/client-value-list", { params }),
				]);

				// Toggling the currency back and forth can land responses out of order; only the
				// newest request is allowed to commit.
				if (requestId !== tariffRequestRef.current) return;

				// Committed together. Three sequential awaits with a setState each left renders in
				// between where the price came from a mix of both currencies' bands.
				setPackageWeightDefaults(
					(weight.data ?? []) as PackageWeightDefaults[]
				);
				setPackageVolumeDefaults(volume.data ?? []);
				setPackageValueDefaults(value.data ?? []);
				setLoadedTariffKey(
					tariffKey(selectedCountryId, currencyParam, zoneParam)
				);
			} catch (err) {
				if (requestId !== tariffRequestRef.current) return;

				// This used to be swallowed with a console.log, which left the stale bands in
				// place — or, on an unseeded EUR table, an empty set that silently priced
				// transport at just the value percentage. Neither is a number to act on.
				console.error("Failed to load tariff bands", err);
				setPackageWeightDefaults([]);
				setPackageVolumeDefaults([]);
				setPackageValueDefaults([]);
				setTariffError(true);
			}
		},
		[dualCurrencyEnabled]
	);

	/**
	 * The (country, currency) pair the form is currently asking to be priced in.
	 *
	 * `||` not `??` on the country: resetStatesToInitial sets selectedCountry to "", which is not
	 * nullish but is not a country either.
	 */
	const tariffCountryId = useMemo(() => {
		const countryForTariffs = selectedCountry || systemCountryId;
		return countryForTariffs ? Number(countryForTariffs) : null;
	}, [selectedCountry, systemCountryId]);

	const tariffCurrencyId = dualCurrencyEnabled ? orderCurrencyId : undefined;

	/**
	 * The destination zone, once it is known.
	 *
	 * `sectionId` is cleared to 0 on every country change and only arrives when the reverse-geocode
	 * resolves the address, so for most of the form's life there is no zone. That is deliberately
	 * not a blocking state: the estimate loads at country level immediately and re-loads, sharpening
	 * to the zone's own bands, the moment a section lands. Because the zone is part of tariffKey the
	 * transition is tracked by the existing staleness gate rather than raced.
	 *
	 * Sent on PostmanAL only. Both zone migrations target AL, so Kosovo's and Macedonia's lookups
	 * still match ZoneId exactly rather than walking the section's parent chain — a section id
	 * there matches no band at all, and the estimate would read a confident 0. The gate is the
	 * DEPLOYMENT's country, not the order's: an AL-registered order bound for Kosovo resolves fine,
	 * because it is PostmanAL's procedures doing the walking.
	 */
	const tariffZoneId = useMemo(
		() =>
			deploymentCountryId === COUNTRY_AL && sectionId
				? Number(sectionId)
				: undefined,
		[deploymentCountryId, sectionId]
	);

	/**
	 * The single owner of tariff loading.
	 *
	 * Four separate places used to call the loader by hand — the buyer-page bootstrap, the country
	 * dropdown, the buyer search and the currency switch — and `resetStatesToInitial` triggered two
	 * of them at once with different currencies. Deriving the load from the pair the form is
	 * actually asking about means there is one request per state, in state order, and no caller can
	 * forget to reload after changing one half of it.
	 *
	 * Waits for the currency list: until it resolves, orderCurrencyId is undefined and
	 * dualCurrencyEnabled is false, so the bands would be fetched for the wrong pair.
	 * PriceConfigurations gates its first fetch the same way.
	 */
	useEffect(() => {
		if (currenciesLoading || tariffCountryId === null) return;
		loadTariffBands(tariffCountryId, tariffCurrencyId, tariffZoneId);
	}, [
		currenciesLoading,
		tariffCountryId,
		tariffCurrencyId,
		tariffZoneId,
		loadTariffBands,
	]);

	const fetchGeoData = useCallback(async (latitude: any, longitude: any) => {
		try {
			const response = await api.get("External/get-geo-data", {
				params: {
					lng: longitude,
					lat: latitude,
					category: CONFIG?.zoneShofer ?? "",
				},
			});
			if (response.status === 200) {
				const result = response?.data;
				const section = Number(result.geofences[0]?.name);
				setSectionId(section);
				// setFinalAddress(result.result);
				setGeoData(response.data);
				setAddressError(false);
				// validateSectionWithCityAndCountry(section);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	const getZones = useCallback(async () => {
		try {
			const allZones = await fetchZones();
			if (allZones) {
				setZones(allZones);
			} else {
				console.error("Failed to fetch zones.");
			}
		} catch (error) {
			console.error("An error occurred while fetching zones:", error);
		}
	}, []);

	const fetchData = useCallback(async () => {
		try {
			if (buyerId) {
				setFromBuyerPage(true);
				const response = await api.get("buyer/unlinked-buyer", {
					params: { buyerId: buyerId },
				});
				const data = response.data?.[0];
				if (data) {
					setTemporaryBuyerDetails(data);
					setTemporaryBuyerDetailsId(data.temporaryBuyerDetailsId);
					setSectionId(data?.sectionId);
					setSelectedCountry(data?.countryId);
					setSelectedCity(data?.cityId);
					setFullName(`${data?.firstName} ${data?.lastName}`);
					setAddress(data?.address);
					setAddressDetails(data?.addressDetails);
					setPhone(data?.phone);
					setEmail(data?.email);
					setApiLatitude(data?.latitude);
					setApiLongitude(data?.longitude);
					setPosition(toMapPosition(data?.latitude, data?.longitude));
					// Tariff bands follow setSelectedCountry above; see the effect that owns them.
					await fetchGeoData(data?.latitude, data?.longitude);
				}
			}
		} catch (error) {
			console.error(
				"An error occurred while fetching buyer data:",
				error
			);
		}
	}, [buyerId, fetchGeoData, loadTariffBands]);

	useEffect(() => {
		getAllCostums();
		getBuyers();
		getVatSelection();
		getZones();
		getSystemDefaultCountryId();
	}, []);

	// fetchData loads the tariff bands, so it has to wait for the currency list: until that
	// resolves, orderCurrencyId is undefined and dualCurrencyEnabled is false, so the request would
	// go out with no currency and the bands would be keyed to the wrong pair. PriceConfigurations
	// gates its first fetch the same way.
	useEffect(() => {
		if (currenciesLoading) return;
		fetchData();
	}, [currenciesLoading, fetchData]);

	const onSelectVat = useCallback(
		(value: any) => {
			const orderPriceIncludeVAT =
				(orderPriceNoVat / 100) * (100 + value);
			setOrderPrice(Number(orderPriceIncludeVAT.toFixed(2)));
			setVat(value);
			setOpenVatModal(false);
		},
		[orderPriceNoVat]
	);

	//ORDER PRICE CHANGE
	const packageValueChange = (e: any) => {
		const inputValue = readDecimalInput(e.target.value);
		if (inputValue === null) return;
		// Emptying the field leaves it empty rather than turning it into 0: the value is required,
		// and an empty field has to read as missing, not as a parcel worth nothing. Every price
		// calculation already treats "" as 0 — it is the field's initial state.
		const value = Number(inputValue) || 0;

		const originalPrice = (value / (100 + vat)) * 100;
		setOrderPriceNoVat(originalPrice);
		setOrderPrice(inputValue);
		setVat(0);
		setPackageValueError(false);
	};

	const ordersRealPriceChange = (e: any) => {
		const inputValue = e.target.value;

		let value = parseFloat(inputValue);

		if (isNaN(value) || inputValue === "") {
			value = 0;
		}
		setOrdersRealPrice(value);
	};

	const resetStatesToInitial = () => {
		fetchData();
		setFirstName("");
		setLastName("");
		setAddress("");
		setAddressDetails("");
		setSectionId(0);
		setPhone("");
		setEmail("");
		setWidth(20);
		setLength(20);
		setHeight(20);
		setWeight(0.5);
		setOpenable(true);
		setFragile(false);
		setDeclared(false);
		setExchangeable(false);
		setInvoice(false);
		setDeliveryInstructions("");
		// Empty, as on a fresh form — not 0, which would count as a filled-in required value.
		setOrderPrice("");
		setOrderPriceNoVat(0);
		setCurrencyId(1);
		// Back to the deployment currency. Leaving the previous choice in place after a save
		// means the next order silently inherits it — and on a form where the currency changes
		// the transport price, a stuck EUR selection prices every following order from the EUR
		// bands without anyone noticing.
		//
		// The bands follow this reset on their own — see the effect that owns tariff loading.
		setChosenOrderCurrencyId(defaultCurrencyId);
		setOrderDescription("");
		setPackageDescription("");
		setSelectedCountry("");
		setSelectedCity("");
		setFullName("");
		setPosition(null);
		setApiLatitude(null);
		setApiLongitude(null);
		setRefId(null);
		setVat(0);
		setCustomsId(null);
		setSearchText("");
		setFromBuyerPage(false);
		setOrdersRealPrice(0);
		setWaitingPickup(false);
		setSelectedFiles([]);
		setDetailsOpen(false);
		setNameError(false);
		setNameCharError(false);
		setAddressError(false);
		setAddressDetailsError(false);
		setPackageValueError(false);
		setPhoneError(false);
		setPackageDescriptionError(false);
		setCountryError(false);
		setCityError(false);
		const urlWithoutQuery = window.location.pathname;
		window.history.pushState({}, document.title, urlWithoutQuery);
		getZones();
		setReturnable(false);
	};

	const validateInsertedData = (data: any) => {
		const {
			firstName,
			lastName,
			address,
			sectionId,
			phone,
			width,
			length,
			height,
			weight,
			orderPrice,
			currencyId,
			orderCurrencyId,
			packageDescription,
			countryId,
		} = data;

		// No customs check: the code is optional, and an order abroad left without one already
		// carries the default by the time it gets here (see orderCustomsId).
		if (
			!firstName ||
			!lastName ||
			!address ||
			!sectionId ||
			!phone ||
			!width ||
			!length ||
			!height ||
			!weight ||
			orderPrice < 0 ||
			!currencyId ||
			!packageDescription ||
			!countryId
		) {
			if (!firstName) {
				toast.error("Emri është i detyrueshëm.");
				setNameError(true);
			}
			if (!lastName) {
				toast.error("Mbiemri është i detyrueshëm.");
				setNameError(true);
			}
			if (!address) {
				toast.error("Adresa është e detyrueshme.");
				setAddressError(true);
			}
			if (!phone) {
				toast.error("Numri i telefonit është i detyrueshëm.");
				setPhoneError(true);
			}
			if (!width) toast.error("Gjerësia është e detyrueshme.");
			if (!length) toast.error("Gjatësia është e detyrueshme.");
			if (!height) toast.error("Lartësia është e detyrueshme.");
			if (!weight) toast.error("Pesha është e detyrueshme.");
			// The dimensions live in the optional group, which starts closed; open it so the empty
			// one shows.
			if (!width || !length || !height || !weight) setDetailsOpen(true);
			if (orderPrice < 0 && !returnable)
				toast.error(
					"Vlera e pakos duhet të jetë më e madhe se zero (0)."
				);
			if (!currencyId) toast.error("Currency ID is required.");
			if (!sectionId)
				toast.error(
					"Kjo zonë nuk është e regjistruar ende.Ju lutem kontaktoni stafin e Postman"
				);
			if (!packageDescription) {
				toast.error("Emërtimi i pakos është i detyrueshëm.");
				setPackageDescriptionError(true);
			}
			if (!countryId) setCountryError(true);

			return false;
		}

		setNameError(false);
		return true;
	};

	const handleFileChange = (event: { fileList: UploadFile[] }) => {
		setSelectedFiles(event.fileList);
	};

	/**
	 * Exchange, payment return and signed invoice are mutually exclusive, so they are one choice
	 * with a "standard" option rather than three switches. The three flags stay as they were —
	 * they are what the save sends.
	 */
	const orderType: OrderType = exchangeable
		? "exchangeable"
		: returnable
		? "returnable"
		: invoice
		? "invoice"
		: "standard";

	const handleOrderTypeChange = (type: OrderType) => {
		setExchangeable(type === "exchangeable");
		setReturnable(type === "returnable");
		setInvoice(type === "invoice");
	};

	const getClientCompanyFinance = async () => {
		try {
			const response = await api.get("Finance/client-finance");
			if (response) {
				const result = response.data[0];
				return result;
			}
		} catch (error) {
			console.log(error);
		}
	};

	const validateOrderPriceChange = async (
		orderPriceParam: number
	): Promise<boolean> => {
		const clientBalanceInPostman = await getClientCompanyFinance();

		// The seller holds two independent balances and nothing is converted between them, so the
		// ceiling has to be the one matching THIS order's currency. Charging a EUR order against
		// the LEK balance would pass a check roughly 96x too generous; charging a LEK order
		// against the EUR balance would refuse almost everything.
		//
		// Off PostmanAL the EUR figure is always 0 and this reduces to the previous check.
		const isEurOrder =
			dualCurrencyEnabled && orderCurrencyId === CURRENCY_EUR;
		const balanceForCurrency = isEurOrder
			? clientBalanceInPostman?.currentBalanceEur ?? 0
			: clientBalanceInPostman?.currentBalance;

		// transportPrice is null while the tariff bands do not match this order's currency. Refuse
		// rather than coalescing to 0: this gate decides whether a seller may create a return, and
		// approving one against an unknown transport cost is the failure mode worth avoiding.
		if (transportPrice === null) {
			return false;
		}

		const clientCurrentBalance = balanceForCurrency - transportPrice;
		if (clientCurrentBalance <= 0) {
			return false;
		}
		if (clientCurrentBalance < /*rowData?.orderPrice*/ orderPriceParam) {
			return false;
		}
		return true;
	};

	/**
	 * The fields the form marks as required, checked before anything else.
	 *
	 * Every missing one is marked at once, with a single toast, and the page scrolls to the first.
	 * Before this, an empty form failed first on the zone lookup — "Kjo Zone nuk eshte e
	 * regjistruar" — because no street meant no section, which told the client nothing about
	 * what to fill in. The checks that follow in handleSaveOrder are unchanged.
	 *
	 * Listed in the order the fields appear on screen, so "the first" is the one nearest the top.
	 */
	const validateRequiredFields = () => {
		const missing = [
			{ id: "name", isMissing: !fullName.trim(), mark: setNameError },
			{ id: "phone", isMissing: !phone, mark: setPhoneError },
			{
				id: "package-name",
				isMissing: !packageDescription,
				mark: setPackageDescriptionError,
			},
			{
				id: "package-value",
				isMissing: orderPrice === "" || orderPrice == null,
				mark: setPackageValueError,
			},
			// Country and city are one field on screen, so both scroll to it.
			{
				id: "location",
				isMissing: !selectedCountry,
				mark: setCountryError,
			},
			{ id: "location", isMissing: !selectedCity, mark: setCityError },
			{
				id: "address-details",
				isMissing: !addressDetails?.trim(),
				mark: setAddressDetailsError,
			},
			{ id: "street", isMissing: !address, mark: setAddressError },
		];
		missing.forEach((field) => field.mark(field.isMissing));

		const firstMissing = missing.find((field) => field.isMissing);
		if (!firstMissing) return true;

		toast.error(strings?.requiredFieldsToast);
		document
			.getElementById(`${fieldId}-${firstMissing.id}`)
			?.scrollIntoView({ behavior: "smooth", block: "center" });
		return false;
	};

	const handleSaveOrder = async () => {
		const fullNameArray = fullName.split(" ");
		const firstName = fullNameArray[0] || "";
		const lastName = fullNameArray.slice(1).join(" ");

		const vatRate = vat / 100;
		const orderPriceNoVat = orderPrice / (1 + vatRate);
		if (!validateRequiredFields()) return;

		// Refuse rather than post a transport price we know nothing about. Sending 0 or a
		// stale-currency figure would be silently wrong on a document the seller is billed from.
		if (transportPrice === null) {
			toast.error(
				tariffError
					? strings?.tariffErrorHint
					: strings?.tariffLoadingHint
			);
			return;
		}

		const existingSection = allSections?.some(
			(item: any) => sectionId !== null && item.id === sectionId
		);
		if (!existingSection) {
			toast.error(
				"Kjo Zone nuk eshte e regjistruar! Ju lutem kontaktoni Postman"
			);
			return;
		}

		if (returnable) {
			const hasSufficientBalance =
				await validateOrderPriceChange(orderPrice);
			if (!hasSufficientBalance) {
				toast.error(
					"Nuk keni balanc të mjaftueshëm për të krijuar kthim të porosisë"
				);
				return;
			}
		}

		// if (validateSectionWithCityAndCountry(sectionId)) {
		if (fromBuyerPage === true) {
			const currentURL = window.location.search;
			const queryParams = extractQueryParameters(currentURL);
			const userId = queryParams.id;

			const formData = new FormData();
			const extractedFiles = selectedFiles.map(
				(file: any) => file?.originFileObj
			);

			extractedFiles.forEach((file, index) => {
				formData.append("Image", file);
			});

			const order: IOrderInsertData = {
				firstName: firstName,
				lastName: lastName,
				address: address,
				addressDetails: addressDetails,
				sectionId: sectionId,
				phone: phone,
				email: email,
				width: width,
				length: length,
				height: height,
				weight: weight,
				openable: openable,
				fragile: fragile,
				declared: declared,
				packageDescription: packageDescription,
				latitude: apiLatitude?.toString(),
				longitude: apiLongitude?.toString(),
				exchangeable: exchangeable,
				invoice: invoice,
				buyerId: userId,
				deliveryInstructions: deliveryInstructions,
				orderPrice: Number(orderPrice),
				transportPrice: transportPrice,
				currencyId: currencyId,
				// The real choice. The legacy currencyId above is still discarded by the API.
				orderCurrencyId,
				orderDescription: orderDescription,
				temporaryBuyerDetailsId: temporaryBuyerDetailsId,
				refId: refId,
				ordersRealPrice: ordersRealPrice,
				orderPriceNoVat: orderPriceNoVat
					? orderPriceNoVat
					: Number(orderPrice),
				vat: vat ?? 0,
				waitingPickup: waitingPickup,
				countryId: selectedCountry,
				// Lets the API keep the section inside this city if the geofence placed it elsewhere.
				cityId: selectedCity,
				customsId: orderCustomsId,
				returnable: returnable,
			};
			for (const key in order) {
				formData.append(key, order?.[key]?.toString() ?? "");
			}

			if (validateInsertedData(order)) {
				if (
					(parseInt(orderPrice) === 0 || !orderPrice) &&
					ordersRealPrice === 0
				) {
					setOpenOrdersRealPriceModal(true);
					toast.error("Ju lutem plotesoni Cmimin real te pakos");
					return;
				} else {
					try {
						const response = await api.post(
							"Order/insert-order",
							formData
						);

						if (response.status === 200) {
							toast.success("Porosia u regjistrua me sukses!");
							resetStatesToInitial();
							setTimeout(() => {
								navigate("/order");
							}, 500);
						} else {
							toast.error(
								"Kjo Zone nuk eshte e regjistruar ende"
							);
							console.error("Failed to submit the order.");
						}
					} catch (error: any) {
						console.error(error.response.data);
					}
				}
			} else {
				return;
			}
		} else {
			const formData = new FormData();

			const data: IOrderInsertData = {
				firstName: firstName,
				lastName: lastName,
				address: address,
				addressDetails: addressDetails,
				sectionId: sectionId,
				phone: phone,
				email: email,
				width: width,
				length: length,
				height: height,
				weight: weight,
				openable: openable,
				fragile: fragile,
				declared: declared,
				exchangeable: exchangeable,
				invoice: invoice,
				deliveryInstructions: deliveryInstructions,
				orderPrice: Number(orderPrice),
				transportPrice: transportPrice,
				currencyId: currencyId,
				// The real choice. The legacy currencyId above is still discarded by the API.
				orderCurrencyId,
				orderDescription: orderDescription,
				packageDescription: packageDescription,
				latitude: apiLatitude,
				longitude: apiLongitude,
				refId: refId,
				ordersRealPrice: ordersRealPrice,
				waitingPickup: waitingPickup,
				orderPriceNoVat: orderPriceNoVat
					? orderPriceNoVat
					: Number(orderPrice),
				vat: vat ?? 0,
				countryId: selectedCountry,
				// Lets the API keep the section inside this city if the geofence placed it elsewhere.
				cityId: selectedCity,
				customsId: orderCustomsId,
				returnable: returnable,
			};

			for (const key in data) {
				formData.append(key, data?.[key]?.toString() ?? "");
			}

			const extractedFiles = selectedFiles.map(
				(file: any) => file?.originFileObj
			);

			extractedFiles.forEach((file, index) => {
				formData.append("Image", file);
			});
			if (validateInsertedData(data)) {
				if (
					(parseInt(orderPrice) === 0 || !orderPrice) &&
					ordersRealPrice === 0
				) {
					setOpenOrdersRealPriceModal(true);
					toast.error("Ju lutem plotesoni Cmimin real te pakos");
					return;
				} else {
					try {
						const response = await api.post(
							"Order/insert",
							formData
						);

						if (response.status === 200) {
							toast.success("Porosia u regjistrua me sukses!");
							resetStatesToInitial();
							setTimeout(() => {
								navigate("/order");
							}, 500);
						} else {
							toast.error(
								"Kjo Zone nuk eshte e regjistruar ende"
							);
							console.error("Failed to submit the order.");
						}
					} catch (error: any) {
						console.error(error.response.data);
					}
				}
			} else {
				return;
			}
		}
		// }
	};

	/** The Register button and the real-price modal's Apply: one save at a time. */
	const onRegisterClick = async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			await handleSaveOrder();
		} finally {
			setIsSaving(false);
		}
	};

	/**
	 * Country and city are picked in one field: choosing a country opens its cities straight away,
	 * and choosing a city completes the pick. `value` is `[countryId, cityId]`, or empty when the
	 * field is cleared.
	 */
	const handleLocationChange = (value?: (string | number)[] | null) => {
		const [countryId, cityId] = (value ?? []).map(Number);
		// Picking the same city again must not wipe the street chosen for it.
		if (
			countryId === Number(selectedCountry) &&
			cityId === Number(selectedCity)
		) {
			return;
		}
		setSelectedCountry(countryId || undefined);
		setSelectedCity(cityId || "");
		setCountryError(false);
		if (cityId) setCityError(false);
		// Another place means another street: clear it, its pin and the zone it resolved to. The
		// street search's country code is derived from selectedCountry — nothing else to keep in
		// step. Tariff bands follow the country and the zone; see the effect that owns them.
		setAddress("");
		setData([]);
		setSectionId(0);
		setPosition(null);
	};

	const handleChange = (address: any) => {
		const selectedLocation = address;
		if (selectedLocation) {
			const lat = selectedLocation.lat;
			const lng = selectedLocation.lng;
			setPosition(toMapPosition(lat, lng));
			setApiLatitude(lat);
			setApiLongitude(lng);
			fetchGeoData(lat, lng);
			setAddress(selectedLocation.label ?? "");
		}
	};

	const clearSearchedBuyer = () => {
		setSectionId(0);
		setSelectedCountry("");
		setSelectedCity("");
		setFullName("");
		setAddress("");
		setAddressDetails("");
		setPhone("");
		setEmail("");
		setSearchText("");
		setPosition(null);
	};

	const setBuyerInputs = (data: any) => {
		setSectionId(data?.sectionId);
		setSelectedCountry(data?.countryId);
		setSelectedCity(data?.cityId);
		setFullName(`${data?.firstName} ${data?.lastName}`);
		setAddress(data?.address);
		setAddressDetails(data?.addressDetails);
		setPhone(data?.phone);
		setEmail(data?.email);
		setApiLatitude(data?.latitude);
		setApiLongitude(data?.longitude);
		// The street search and the city list follow selectedCountry on their own; only the pin
		// has to be moved by hand.
		setPosition(toMapPosition(data?.latitude, data?.longitude));
	};

	// Shared by every searchable select on the form, so it only asks for a label.
	const filterOption = (input: string, option?: { label?: unknown }) =>
		searchKey(option?.label).includes(searchKey(input));

	// The country-and-city search: typing a city finds it without picking the country first.
	const filterLocation = (input: string, path: { label?: unknown }[]) =>
		path.some((option) =>
			searchKey(option.label).includes(searchKey(input))
		);

	const onBuyerSeachChange = async (value: string) => {
		setSearchText(value);
		const selectedBuyer = buyerDropDown?.find(
			(item: any) => item.id === value
		);
		setBuyerInputs(selectedBuyer);
		setNameError(false);
		setNameCharError(false);
		setAddressError(false);
		setAddressDetailsError(false);
		setPhoneError(false);
		setCountryError(false);
		setCityError(false);
		setPackageDescriptionError(false);
		setCustomsId(null);
		// setBuyerInputs sets the country; the tariff effect reloads the bands from there.
	};

	const getAllCostums = async () => {
		try {
			const response = await api.get("ListOptions/get-costums");
			if (response.status === 200) {
				const result = response?.data;
				setCostums(result);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const renderCostumOptions = () => {
		const costumOptions = costums.map((costum: any) => ({
			value: costum.id,
			label: customsLabel(costum),
		}));

		return [...costumOptions];
	};

	const getPackageVolume = (
		volume: number,
		packageVolumeDefaults: PackageWeightDefaults[]
	) => {
		const packageVolume = packageVolumeDefaults.find((pv) =>
			pv.to != null
				? pv.from <= volume && pv?.to > volume
				: pv.from <= volume
		);
		return packageVolume;
	};
	const getPackageValue = (
		packageValuePrice: number,
		packageValueDefaults: PackageValueDefaults[]
	) =>
		packageValueDefaults?.find((pv) =>
			pv.to != null
				? pv.from <= packageValuePrice && pv?.to > packageValuePrice
				: pv.from <= packageValuePrice
		);
	const getPackageWeight = (
		weight: number,
		packageWeightDefaults: PackageWeightDefaults[]
	) =>
		packageWeightDefaults?.find((pv) =>
			pv.to != null
				? pv.from <= weight && pv?.to > weight
				: pv.from <= weight
		);

	const packageValue = useMemo<PackageValueDefaults | undefined>(
		() =>
			getPackageValue(
				orderPrice > ordersRealPrice ? orderPrice : ordersRealPrice,
				packageValueDefaults
			),
		[orderPrice, ordersRealPrice, packageValueDefaults]
	);

	const calculatePricePackageVolume = (
		packageVolumePrice: number,
		packageWeightPrice: number,
		packageValuePrice: number,
		packageValuePercentage: number
	) => {
		const priceByVolume = packageVolumePrice ?? 0;
		const priceByWeight = packageWeightPrice ?? 0;
		const priceByValue =
			packageValuePrice * ((packageValuePercentage ?? 0) / 100);

		return priceByVolume + priceByWeight + priceByValue;
	};

	const volume = width * length * height;

	const packageVolume = useMemo<PackageWeightDefaults | undefined>(
		() => getPackageVolume(volume, packageVolumeDefaults),
		[volume, packageVolumeDefaults]
	);

	const packageWeight = useMemo(
		() => getPackageWeight(weight, packageWeightDefaults),
		[packageWeightDefaults, weight]
	);

	/**
	 * True only when the bands in state belong to the country, currency and zone the form is
	 * actually asking about.
	 */
	const tariffsReady =
		tariffCountryId !== null &&
		loadedTariffKey ===
			tariffKey(tariffCountryId, tariffCurrencyId, tariffZoneId);

	/**
	 * Null rather than a number whenever the bands cannot price this order.
	 *
	 * Previously this always produced *some* figure: mid-switch it mixed the two currencies' bands,
	 * and against an empty band set it quietly collapsed to just the value percentage. Both look
	 * like prices. Returning null forces every consumer — the summary, the returnable-balance gate
	 * and the save payload — to deal with "not known yet" explicitly.
	 */
	const transportPrice = useMemo(() => {
		if (!tariffsReady) return null;
		return calculatePricePackageVolume(
			packageVolume?.price as number,
			packageWeight?.price as number,
			orderPrice > ordersRealPrice ? orderPrice : ordersRealPrice,
			packageValue?.percentage as number
		);
	}, [
		tariffsReady,
		packageVolume?.price,
		packageWeight?.price,
		orderPrice,
		ordersRealPrice,
		packageValue?.percentage,
	]);

	// The size and weight states start as numbers and hold the field's text once edited, as they
	// always have; the arithmetic coerces it. readDecimalInput admits no minus sign, so a negative
	// value can no longer be typed in the first place.
	const onDecimalChange = (set: (value: any) => void) => (e: any) => {
		const value = readDecimalInput(e.target.value);
		if (value !== null) set(value);
	};

	const onWidthChange = onDecimalChange(setWidth);
	const onLengthChange = onDecimalChange(setLength);
	const onHeightChange = onDecimalChange(setHeight);
	const onWeightChange = onDecimalChange(setWeight);

	// Cleared, the field goes back to empty — which, on an order abroad, means the default code.
	const onCustomSelect = (value: any) => {
		setCustomsId(value ?? null);
	};

	/**
	 * Letters and spaces only, as before — but a refused keystroke is now just not applied, where it
	 * used to wipe the whole name. Only the text an edit adds is checked, so deleting from, or
	 * adding to, a saved buyer's name that holds other characters still works.
	 */
	const onFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (!FULL_NAME_ALLOWED.test(insertedText(fullName, value))) {
			setNameCharError(true);
			return;
		}
		setFullName(value);
		setNameError(false);
		setNameCharError(false);
	};

	const { strings } = useLang("registerOrderForm");

	const buyerOptions = useMemo(
		() =>
			buyerDropDown?.map((buyer: any) => ({
				value: buyer.id,
				label: `${buyer.firstName} ${buyer.lastName} (${buyer.phone})`,
			})),
		[buyerDropDown]
	);

	const orderTypeOptions: OrderTypeOption<OrderType>[] = [
		{
			value: "standard",
			icon: <InboxOutlined />,
			label: strings?.orderTypeStandard,
			description: strings?.orderTypeStandardDescription,
		},
		{
			value: "exchangeable",
			icon: <ExchangeIcon />,
			label: strings?.exchangeableLabel,
			description: strings?.orderTypeExchangeDescription,
		},
		{
			value: "returnable",
			icon: <ReturnableIcon />,
			label: strings?.returnableLabel,
			description: strings?.orderTypeReturnDescription,
		},
		{
			value: "invoice",
			icon: <InvoiceIcon />,
			label: strings?.invoiceLabel,
			description: strings?.orderTypeInvoiceDescription,
		},
	];

	// How many of the optional fields hold something, for the closed group's badge. Size and weight
	// always hold their defaults, so they are not counted.
	const detailsFilledCount = [
		refId,
		orderDescription,
		deliveryInstructions,
		selectedFiles.length > 0,
		waitingPickup,
		fragile,
		declared,
	].filter(Boolean).length;

	const requiredError = (hasError: boolean) =>
		hasError ? strings?.requiredFieldError : undefined;

	// [country, city] for the country-and-city field — only the country while no city is set.
	const locationValue = selectedCountry
		? selectedCity
			? [Number(selectedCountry), Number(selectedCity)]
			: [Number(selectedCountry)]
		: undefined;

	return (
		<ConfigProvider theme={FORM_THEME}>
			<div className={styles.page}>
				<div className={styles.shell}>
					<div className={styles.layout}>
						<div className={styles.main}>
							{/*
							 * Every required field in one card, in the order they are filled in: buyer,
							 * then the address (country and city, details, street), then the package
							 * name and value. An order abroad adds the (optional) customs code as the
							 * last input. The map that confirms the street closes the card, after every
							 * input, so on a phone it never sits between fields. No section titles or
							 * explanations — each label says what it wants.
							 */}
							<FormSection>
								<Select
									id={`${fieldId}-buyer`}
									className={styles.control}
									aria-label={strings?.searchLabel}
									showSearch
									allowClear
									value={searchText || undefined}
									placeholder={strings?.searchPlaceholder}
									suffixIcon={<SearchOutlined />}
									options={buyerOptions}
									filterOption={filterOption}
									onChange={onBuyerSeachChange}
									onClear={clearSearchedBuyer}
								/>
								<div
									className={styles.divider}
									aria-hidden="true"
								/>
								<div className={styles.grid}>
									<FormField
										label={strings?.fullNameLabel}
										htmlFor={`${fieldId}-name`}
										required
										error={
											nameCharError
												? strings?.nameCharsHint
												: nameError && fullName.trim()
												? // Flagged while filled in: the save found
												  // no last name after the first word.
												  strings?.lastNameRequired
												: requiredError(nameError)
										}
									>
										<Input
											id={`${fieldId}-name`}
											value={fullName}
											onChange={onFullNameChange}
											placeholder={
												strings?.fullNamePlaceholder
											}
											autoComplete="off"
											status={
												nameError || nameCharError
													? "error"
													: ""
											}
										/>
									</FormField>
									<FormField
										label={strings?.phoneLabel}
										htmlFor={`${fieldId}-phone`}
										required
										error={requiredError(phoneError)}
									>
										<Input
											id={`${fieldId}-phone`}
											value={phone}
											onChange={(e) => {
												setPhone(e.target.value);
												setPhoneError(false);
											}}
											placeholder={
												strings?.phonePlaceholder
											}
											inputMode="tel"
											autoComplete="off"
											status={phoneError ? "error" : ""}
										/>
									</FormField>
								</div>
								<div className={styles.grid}>
									<FormField
										label={strings?.locationLabel}
										htmlFor={`${fieldId}-location`}
										required
										error={requiredError(
											countryError || cityError
										)}
									>
										{/* Picking a country opens its cities beside it; typing a city
										    finds it directly. */}
										<Cascader
											id={`${fieldId}-location`}
											className={styles.control}
											popupClassName={
												styles.locationPopup
											}
											options={locationOptions}
											value={locationValue}
											onChange={handleLocationChange}
											placeholder={
												strings?.locationPlaceholder
											}
											showSearch={{
												filter: filterLocation,
											}}
											{...NO_BROWSER_AUTOFILL}
											allowClear
											status={
												countryError || cityError
													? "error"
													: ""
											}
										/>
									</FormField>
									<FormField
										label={strings?.addressDetailsLabel}
										htmlFor={`${fieldId}-address-details`}
										required
										error={requiredError(
											addressDetailsError
										)}
									>
										<Input
											id={`${fieldId}-address-details`}
											value={addressDetails ?? ""}
											onChange={(e) => {
												setAddressDetails(
													e.target.value
												);
												setAddressDetailsError(false);
											}}
											placeholder={
												strings?.addressDetailsPlaceholder
											}
											autoComplete="off"
											status={
												addressDetailsError
													? "error"
													: ""
											}
										/>
									</FormField>
								</div>
								<FormField
									label={strings?.addressLabel}
									htmlFor={`${fieldId}-street`}
									required
									error={requiredError(addressError)}
								>
									<AddressSelect
										id={`${fieldId}-street`}
										onAddressChange={handleChange}
										country={addressSearchCountry}
										selectedAddress={address || undefined}
										city={selectedCity}
										disabled={
											!addressSearchCountry ||
											!selectedCity
										}
										placeholder={
											addressSearchCountry && selectedCity
												? strings?.streetSearchPlaceholder
												: strings?.streetDisabledPlaceholder
										}
										status={addressError ? "error" : ""}
										style={{ width: "100%" }}
									/>
								</FormField>
								<div className={styles.packageRow}>
									<FormField
										className={styles.grow}
										label={strings?.packageDescriptionLabel}
										htmlFor={`${fieldId}-package-name`}
										required
										error={requiredError(
											packageDescriptionError
										)}
									>
										<Input
											id={`${fieldId}-package-name`}
											value={packageDescription}
											onChange={(e) => {
												setPackageDescription(
													e.target.value
												);
												setPackageDescriptionError(
													false
												);
											}}
											placeholder={
												strings?.packageDescriptionPlaceholder
											}
											status={
												packageDescriptionError
													? "error"
													: ""
											}
										/>
									</FormField>
									{/*
									 * The order's currency, as its own field rather than a toggle
									 * tucked onto the value input's label row.
									 *
									 * The choice re-prices the entire order — it picks which tariff
									 * band set the transport cost comes from — so it has to be as
									 * visible as any other decision on this form. It sits BEFORE the
									 * value input because that is the order the operator thinks in:
									 * which currency, then how much.
									 *
									 * The component renders nothing when there is only one currency,
									 * so Kosovo and Macedonia see the row without it.
									 *
									 * It locks — rather than disappears — on a delivery outside the
									 * deployment's country, where EUR is the only currency the order
									 * can be settled in. Still visible, still stating the currency,
									 * with the hint saying why it cannot be changed.
									 */}
									<CurrencySelectField
										className={styles.currencyField}
										label={strings?.orderCurrencyLabel}
										currencies={selectableCurrencies}
										value={orderCurrencyId}
										onChange={setChosenOrderCurrencyId}
										disabled={currencyLocked}
										hint={
											currencyLocked ? (
												<div
													className={
														styles.currencyLockedHint
													}
												>
													{strings?.currencyLockedHint ??
														"Vetëm EURO jashtë Shqipërisë"}
												</div>
											) : undefined
										}
									/>
									<FormField
										className={styles.grow}
										label={strings?.packageValueLabel}
										htmlFor={`${fieldId}-package-value`}
										required
										error={requiredError(packageValueError)}
										hint={
											dualCurrencyEnabled ? (
												<div
													className={
														styles.currencyHint
													}
												>
													{convertedOrderPrice !==
													null ? (
														<>
															<span
																className={
																	styles.currencyHintLabel
																}
															>
																{strings?.currencyHintPrefix ??
																	"Vlera në"}{" "}
																{currencySymbol(
																	otherCurrencyId
																)}
																:
															</span>{" "}
															<strong>
																{convertedOrderPrice.toFixed(
																	2
																)}{" "}
																{currencySymbol(
																	otherCurrencyId
																)}
															</strong>
														</>
													) : (
														<span
															className={
																styles.currencyHintLabel
															}
														>
															{strings?.currencyHintEmpty ??
																"Porosia regjistrohet në"}{" "}
															{currencySymbol(
																orderCurrencyId
															)}
														</span>
													)}
												</div>
											) : undefined
										}
									>
										<Input
											id={`${fieldId}-package-value`}
											type="text"
											inputMode="decimal"
											value={orderPrice}
											onChange={packageValueChange}
											placeholder={
												strings?.packageValuePlaceholder
											}
											suffix={currencySymbol(
												orderCurrencyId
											)}
											status={
												packageValueError ? "error" : ""
											}
										/>
									</FormField>
								</div>
								{/* Orders abroad only, and the last input: it is optional, and left
								    empty the order goes with the default code its placeholder shows. */}
								{isInternational && (
									<FormField
										label={strings?.customsCodeLabel}
										htmlFor={`${fieldId}-customs`}
									>
										<Select
											id={`${fieldId}-customs`}
											className={styles.control}
											showSearch
											allowClear
											// undefined, not null: antd hides the placeholder for null.
											value={customsId ?? undefined}
											placeholder={
												defaultCustoms
													? customsLabel(
															defaultCustoms
													  )
													: strings?.customsCodePlaceholder
											}
											options={renderCostumOptions()}
											filterOption={filterOption}
											onChange={onCustomSelect}
										/>
									</FormField>
								)}
								<DeliveryMap
									position={position}
									placeholder={strings?.mapPlaceholder}
								/>
							</FormSection>

							<FormSection title={strings?.orderTypeTitle}>
								<OrderTypePicker
									name={`${fieldId}-order-type`}
									ariaLabel={strings?.orderTypeTitle}
									value={orderType}
									options={orderTypeOptions}
									onChange={handleOrderTypeChange}
								/>
							</FormSection>

							{/* Every optional field, one click away. */}
							<CollapsibleSection
								title={strings?.detailsTitle}
								optionalTag={strings?.optionalTag}
								icon={<FileTextOutlined />}
								summary={strings?.detailsSummary}
								badge={
									detailsFilledCount > 0
										? strings?.filledBadge?.replace(
												"{count}",
												String(detailsFilledCount)
										  )
										: undefined
								}
								open={detailsOpen}
								onToggle={() => setDetailsOpen((open) => !open)}
							>
								<div className={styles.grid}>
									<FormField
										label={strings?.refIdLabel}
										htmlFor={`${fieldId}-ref`}
									>
										<Input
											id={`${fieldId}-ref`}
											value={refId ?? ""}
											onChange={(e) =>
												setRefId(e.target.value)
											}
											placeholder={
												strings?.refIdPlaceholder
											}
										/>
									</FormField>
									<FormField label={strings?.photosLabel}>
										<Upload
											fileList={selectedFiles}
											onChange={handleFileChange}
											multiple
											accept="image/*"
											listType="picture"
											beforeUpload={() => false}
										>
											<Button icon={<UploadOutlined />}>
												{strings?.uploadPhotos}
											</Button>
										</Upload>
									</FormField>
								</div>
								<div className={styles.grid}>
									<FormField
										label={
											strings?.packageDescriptionAreaLabel
										}
										htmlFor={`${fieldId}-description`}
									>
										<TextArea
											id={`${fieldId}-description`}
											autoSize={{
												minRows: 2,
												maxRows: 6,
											}}
											value={orderDescription}
											onChange={(e) =>
												setOrderDescription(
													e.target.value
												)
											}
											placeholder={
												strings?.packageDescriptionAreaPlaceholder
											}
										/>
									</FormField>
									<FormField
										label={
											strings?.deliveryInstructionsLabel
										}
										htmlFor={`${fieldId}-instructions`}
									>
										<TextArea
											id={`${fieldId}-instructions`}
											autoSize={{
												minRows: 2,
												maxRows: 6,
											}}
											value={deliveryInstructions}
											onChange={(e) =>
												setDeliveryInstructions(
													e.target.value
												)
											}
											placeholder={
												strings?.deliveryInstructionsPlaceholder
											}
										/>
									</FormField>
								</div>
								<div className={styles.checkboxes}>
									<Checkbox
										checked={waitingPickup}
										onChange={(e) =>
											setWaitingPickup(e.target.checked)
										}
									>
										{strings?.readyForPickupLabel}
									</Checkbox>
									<Checkbox
										checked={fragile}
										onChange={(e) =>
											setFragile(e.target.checked)
										}
									>
										{strings?.fragileLabel}
									</Checkbox>
									<Checkbox
										checked={declared}
										onChange={(e) =>
											setDeclared(e.target.checked)
										}
									>
										{strings?.declaredLabel}
									</Checkbox>
								</div>
								{/* Last: most parcels keep the default size and weight. */}
								<div
									className={styles.subGroup}
									role="group"
									aria-labelledby={`${fieldId}-dimensions`}
								>
									<h3
										id={`${fieldId}-dimensions`}
										className={styles.subGroupTitle}
									>
										{strings?.dimensionsTitle}
									</h3>
									<div className={styles.grid4}>
										<FormField
											label={strings?.widthLabel}
											htmlFor={`${fieldId}-width`}
										>
											<Input
												id={`${fieldId}-width`}
												type="text"
												inputMode="decimal"
												value={width}
												onChange={onWidthChange}
												suffix="cm"
											/>
										</FormField>
										<FormField
											label={strings?.lengthLabel}
											htmlFor={`${fieldId}-length`}
										>
											<Input
												id={`${fieldId}-length`}
												type="text"
												inputMode="decimal"
												value={length}
												onChange={onLengthChange}
												suffix="cm"
											/>
										</FormField>
										<FormField
											label={strings?.heightLabel}
											htmlFor={`${fieldId}-height`}
										>
											<Input
												id={`${fieldId}-height`}
												type="text"
												inputMode="decimal"
												value={height}
												onChange={onHeightChange}
												suffix="cm"
											/>
										</FormField>
										<FormField
											label={strings?.weightLabel}
											htmlFor={`${fieldId}-weight`}
										>
											<Input
												id={`${fieldId}-weight`}
												type="text"
												inputMode="decimal"
												value={weight}
												onChange={onWeightChange}
												suffix="kg"
											/>
										</FormField>
									</div>
								</div>
							</CollapsibleSection>
						</div>

						<aside className={styles.aside}>
							<PriceSummary
								currencyId={orderCurrencyId}
								orderPrice={orderPrice}
								transportPrice={transportPrice}
								tariffError={tariffError}
								returnable={returnable}
								strings={strings}
							/>
							<div className={styles.actions}>
								<Button
									onClick={resetStatesToInitial}
									disabled={isSaving}
								>
									{strings?.cancelButtonText}
								</Button>
								{/* Painted inline: Tailwind's preflight resets every button's background
								    to transparent and wins over antd's primary fill, which left a white
								    label on a white button. It never disables — loading only fades it —
								    so the fill needs no per-state variant (see W-D32). */}
								<Button
									type="primary"
									style={PRIMARY_BUTTON_STYLE}
									onClick={onRegisterClick}
									loading={isSaving}
								>
									{strings?.registerButtonText}
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</div>

			<Modal
				title={strings?.vatModalTitle}
				open={openVatModal}
				onCancel={() => setOpenVatModal(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
			>
				<Divider />
				<div className="flex flex-row justify-around">
					{vatValues.map((vatOption: any) => (
						<Button
							key={vatOption.id}
							className={
								vat === vatOption.value
									? styles.selectedVat
									: undefined
							}
							type="default"
							onClick={() => onSelectVat(vatOption.value)}
						>
							{vatOption.value}%
						</Button>
					))}
				</div>
			</Modal>

			<Modal
				title={"Porosia nuk regjistohet!"}
				open={openOrdersRealPriceModal}
				okButtonProps={{ style: { display: "none" } }}
				cancelButtonProps={{ style: { display: "none" } }}
				onCancel={() => {
					setOrdersRealPrice(0);
					setOpenOrdersRealPriceModal(false);
				}}
			>
				<div className="flex flex-col gap-8">
					<div className="custom-alert custom-alert-error">
						<div className="custom-alert-icon">{<AlertIcon />}</div>
						<div className="custom-alert-content">
							<div>
								<p>
									<b>
										{" "}
										{"Porosia nuk mund të regjistrohet! "}
									</b>
									{"Pavarësisht se vlera e pakos është "}{" "}
									<b>0{currencySymbol(orderCurrencyId)}</b>
									{
										" dhe blerësi juaj nuk do të paguaj asgjë, ne duhet të dijmë vlerën reale të porosisë.Kjo vlerë nuk shfaqet në etiketë"
									}
								</p>
							</div>
						</div>
					</div>
					<div className={styles.modalBody}>
						{/* Both figures belong to the order being registered, so they carry the
						    order's currency rather than the deployment's. */}
						<DefaultInput
							className={styles.modalInput}
							onChange={ordersRealPriceChange}
							label={strings?.realPackageValueLabel}
							value={ordersRealPrice}
							suffix={currencySymbol(orderCurrencyId)}
							type="number"
							min={0}
						/>
						<p className={styles.readOnlyLine}>
							{strings?.transportCostLabel}:{" "}
							<strong>
								{transportPrice === null
									? tariffError
										? strings?.tariffErrorHint
										: strings?.tariffLoadingHint
									: `${transportPrice.toFixed(
											2
									  )} ${currencySymbol(orderCurrencyId)}`}
							</strong>
						</p>
					</div>
					<Buttons
						button1Text="Mbyll"
						onButton1Click={() => {
							setOrdersRealPrice(0);
							setOpenOrdersRealPriceModal(false);
						}}
						button2Text="Apliko"
						onButton2Click={onRegisterClick}
						disabled={isSaving}
					/>
				</div>
			</Modal>
		</ConfigProvider>
	);
};

export default Register;
